import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { PermissionDefinition, Role } from '../../core/models/user.models';
import { RolesService } from '../../core/services/roles.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { SubscriptionPlansService } from '../../core/services/subscription-plans.service';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-roles',
  imports: [FormsModule, RouterLink],
  templateUrl: './roles.html',
  styleUrl: './roles.scss',
})
export class Roles implements OnInit {
  private readonly rolesService = inject(RolesService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly plansService = inject(SubscriptionPlansService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  newRole = {
    name: '',
    description: '',
  };

  roles: Role[] = [];
  permissionCounts: Record<string, number> = {};

  isLoading = false;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  allowCustomRoles = false;
  isPlatformAdmin = false;
  permissionRole: Role | null = null;
  rolePermissions: PermissionDefinition[] = [];
  isRolePermissionsLoading = false;

  get isPlatformScope(): boolean {
    return this.isPlatformAdmin;
  }
  get canCreateRole(): boolean { return this.currentUser.hasPermission('ROLE_CREATE'); }
  get canAssignPermissions(): boolean { return this.currentUser.hasPermission('PERMISSION_ASSIGN'); }

  isProtectedSuperAdminRole(role: Role): boolean {
    const normalizedName = role.name.replace(/[\s_-]/g, '').toLowerCase();
    return normalizedName === 'superadmin' || normalizedName === 'platformsuperadmin';
  }

  get permissionsRoute(): string {
    return this.isPlatformAdmin ? '/admin/permissions' : '/business/permissions';
  }

  get groupedRolePermissions(): Array<{ module: string; permissions: PermissionDefinition[] }> {
    const groups = new Map<string, PermissionDefinition[]>();
    for (const permission of this.rolePermissions) {
      const module = permission.module || 'Other';
      groups.set(module, [...(groups.get(module) ?? []), permission]);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([module, permissions]) => ({ module, permissions }));
  }

  ngOnInit(): void {
    const roleNames = this.currentUser.user()?.roles ?? [this.currentUser.user()?.role ?? ''];
    this.isPlatformAdmin = roleNames.some((role) => role === 'SuperAdmin' || role === 'PlatformSuperAdmin');
    if (this.isPlatformAdmin) {
      this.allowCustomRoles = true;
      this.loadRoles();
      return;
    }
    this.loadRoles();
    this.plansService.mine().subscribe({
      next: (subscription) => this.allowCustomRoles = Boolean(subscription?.plan?.allowCustomRoles),
      error: () => this.allowCustomRoles = false,
    });
  }

  loadRoles(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.rolesService.list()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
      next: (roles) => {
        this.roles = roles;
        this.loadPermissionCounts(roles);
        this.isLoading = false;
        this.renderNow();
      },
      error: (error: unknown) => {
        this.isLoading = false;
        this.errorMessage = httpErrorMessage(error, 'Roles API is unavailable.');
        this.renderNow();
      },
    });
  }

  private loadPermissionCounts(roles: Role[]): void {
    this.permissionCounts = {};
    if (!roles.length) return;
    forkJoin(roles.map((role) => this.rolesService.permissionIds(role.id).pipe(catchError(() => of([]))))).subscribe({
      next: (permissionIdsByRole) => {
        this.permissionCounts = Object.fromEntries(
          roles.map((role, index) => [role.id, permissionIdsByRole[index]?.length ?? 0]),
        );
        this.renderNow();
      },
    });
  }

  createRole(): void {
    if (!this.allowCustomRoles) {
      this.errorMessage = 'Custom roles are not included in your current subscription plan.';
      return;
    }
    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.rolesService.create({ ...this.newRole, tenantId: undefined })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
      next: (role) => {
        this.roles = [role, ...this.roles];
        this.permissionCounts = { ...this.permissionCounts, [role.id]: 0 };
        this.newRole = { name: '', description: '' };
        this.successMessage = 'Role created.';
        this.renderNow();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to create role.');
        this.renderNow();
      },
    });
  }
  viewPermissions(role: Role): void {
    this.permissionRole = role;
    this.rolePermissions = [];
    this.isRolePermissionsLoading = true;
    this.renderNow();
    forkJoin({ permissionIds: this.rolesService.permissionIds(role.id), definitions: this.permissionsService.list() }).subscribe({
      next: ({ permissionIds, definitions }) => {
        const selected = new Set(permissionIds);
        this.rolePermissions = definitions.filter((permission) => selected.has(permission.id));
        this.isRolePermissionsLoading = false;
        this.renderNow();
      },
      error: (error: unknown) => {
        this.isRolePermissionsLoading = false;
        this.errorMessage = httpErrorMessage(error, `Unable to load permissions for ${role.name}.`);
        this.renderNow();
      },
    });
  }

  closePermissionView(): void {
    this.permissionRole = null;
    this.rolePermissions = [];
    this.renderNow();
  }

  private renderNow(): void {
    this.changeDetector.detectChanges();
  }
}
